// =====================================================
// PARTICIPANT COLUMNS
// Stage 2: Participants & Pricing
// Column definitions for participants DataTable (row click opens the detail dialog)
// =====================================================

import { formatBs } from '@/lib/money';
import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Globe, PenLine, Receipt, ShieldCheck, Users, Vote } from 'lucide-react';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { HintTip } from './HintTip';
import { needsSignature } from '../public/registrationEditLogic';
import { RowActionButton } from './RowActionButton';
import { StatusPill } from './StatusPill';
import { attendanceLook, paymentLook, registrationLook } from './participantStatus';
import { StopRowClick as Stop } from './StopRowClick';
import { ParticipantActionsMenu } from './ParticipantActionsMenu';
import type { ParticipantActionGroup, ParticipantReceiptInfo } from './participantActions';
import { SortableColumnHeader } from '@/components/molecules/table';

interface ParticipantColumnsOptions {
  /** Row actions (⋮ menu); the same list feeds the right-click menu */
  getActions: (participant: ParticipantWithDetails) => ParticipantActionGroup[];
  onRequestPayment: (participant: ParticipantWithDetails) => void;
  onReview: (participant: ParticipantWithDetails) => void;
  /** Receipts of the participant's recorded payments (undefined while unknown / not permitted) */
  getReceiptInfo: (participant: ParticipantWithDetails) => ParticipantReceiptInfo | undefined;
  onIssueReceipt: (
    participant: ParticipantWithDetails,
    payment: NonNullable<ParticipantReceiptInfo['paymentWithoutReceipt']>,
  ) => void;
  canIssueReceipts: boolean;
  /** Digital-signature event: unsigned rows get a pen icon that opens the edit-link dialog */
  signatureMode?: string | null;
  onRequestSignature: (participant: ParticipantWithDetails) => void;
  canEditForm: boolean;
  /** The event has an election: flag participants whose "Habilitado para votar" differs from the default */
  showVoting?: boolean;
  isUpdating: boolean;
  /** Payments + attendance allowed (event OPEN or CLOSED) */
  canOperate?: boolean;
  /** Row selection for bulk actions */
  selection?: {
    isSelected: (id: string) => boolean;
    toggle: (id: string) => void;
    pageState: 'all' | 'some' | 'none';
    togglePage: () => void;
  };
}

export function getParticipantColumns(options: ParticipantColumnsOptions): ColumnDef<ParticipantWithDetails>[] {
  const { getActions, onRequestPayment, onReview, getReceiptInfo, onIssueReceipt, canIssueReceipts, signatureMode, onRequestSignature, canEditForm, showVoting = false, isUpdating, canOperate = true, selection } = options;

  const selectColumn: ColumnDef<ParticipantWithDetails>[] = selection
    ? [
        {
          id: 'select',
          header: () => (
            <Checkbox
              checked={selection.pageState === 'all' ? true : selection.pageState === 'some' ? 'indeterminate' : false}
              onCheckedChange={selection.togglePage}
              aria-label="Seleccionar página"
            />
          ),
          cell: ({ row }) => (
            <Stop>
              <Checkbox
                checked={selection.isSelected(row.original.id)}
                onCheckedChange={() => selection.toggle(row.original.id)}
                aria-label="Seleccionar participante"
              />
            </Stop>
          ),
          size: 40,
          enableSorting: false,
          // Pinned at the left edge; fixed 40px so the name column can pin right after it
          meta: { stickyLeft: 0, className: 'w-10 min-w-10 max-w-10 px-3' },
        },
      ]
    : [];

  return [
    ...selectColumn,
    {
      id: 'name',
      header: ({ column }) => <SortableColumnHeader column={column} title="Nombre" />,
      accessorFn: (row) => {
        const isMinister = !!row.minister_id;
        return isMinister ? row.minister?.full_name : row.guest?.full_name;
      },
      cell: ({ row }) => {
        const p = row.original;
        const isMinister = !!p.minister_id;
        const name = isMinister ? p.minister?.full_name : p.guest?.full_name;
        const guest = p.guest;
        const isDelegate = !!guest?.is_delegate;
        // Who a delegate stands in for: a registered minister, else the free-text name / institution
        const representing =
          guest?.representing_minister?.full_name ?? guest?.represented_name ?? guest?.institution_note ?? null;

        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="max-w-[200px] truncate font-medium" title={name ?? undefined}>
                {name}
              </span>
              {p.is_mandatory && (
                <span title="Participante obligatorio">
                  <ShieldCheck className="size-3.5 text-primary" aria-label="Obligatorio" />
                </span>
              )}
              {!isMinister && (
                <Badge variant={isDelegate ? 'default' : 'secondary'} className="text-xs">
                  {isDelegate ? 'Delegado' : 'Invitado'}
                </Badge>
              )}
            </div>
            {p.minister?.adb_code && <span className="text-xs text-muted-foreground">{p.minister.adb_code}</span>}
            {/* Only the exceptions are flagged: a minister or delegate who cannot vote, a guest who can */}
            {showVoting && p.voting_enabled === false && (isMinister || isDelegate) && (
              <Badge variant="outline" className="w-fit text-xs text-muted-foreground">
                <Vote className="mr-1 size-3" /> No vota
              </Badge>
            )}
            {showVoting && p.voting_enabled === true && !isMinister && !isDelegate && (
              <Badge variant="outline" className="w-fit text-xs">
                <Vote className="mr-1 size-3" /> Habilitado para votar
              </Badge>
            )}
            {isDelegate && (
              <Badge variant="outline" className="w-fit max-w-[220px] text-xs">
                <Users className="mr-1 size-3 shrink-0" />
                <span className="truncate">Representa a {representing ?? 'otro ministro / institución'}</span>
              </Badge>
            )}
          </div>
        );
      },
      size: 240,
      // Stays visible while scrolling sideways (after the 40px checkbox column, when present)
      meta: { stickyLeft: selection ? 40 : 0 },
    },
    {
      id: 'location',
      header: ({ column }) => <SortableColumnHeader column={column} title="Distrito / Región" />,
      accessorFn: (row) => row.minister?.district?.name || '-',
      cell: ({ row }) => {
        const district = row.original.minister?.district?.name;
        const region = row.original.minister?.region?.name;
        if (!district && !region) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="flex flex-col">
            <span>{district ?? '-'}</span>
            {region && <span className="text-xs text-muted-foreground">{region}</span>}
          </div>
        );
      },
      size: 170,
    },
    {
      id: 'price',
      header: ({ column }) => <SortableColumnHeader column={column} title="Precio" />,
      accessorFn: (row) =>
        row.payment_status === 'PAID' ? row.payment_amount : row.calculated_price,
      cell: ({ row }) => {
        const { payment_status, payment_amount, calculated_price, pricing_rule } = row.original;
        const isPaid = payment_status === 'PAID';
        const displayPrice = isPaid ? payment_amount : calculated_price;
        const bs = formatBs;
        // Details that used to add lines under the amount now live in a hint
        const details = [
          pricing_rule?.name && `Regla: ${pricing_rule.name}`,
          payment_status === 'PARTIAL' && `Pagado ${bs(payment_amount)}`,
          isPaid && payment_amount !== calculated_price && `Precio calculado ${bs(calculated_price)}`,
        ].filter(Boolean) as string[];
        const amount = <span className="whitespace-nowrap font-medium">{bs(displayPrice)}</span>;
        if (details.length === 0) return amount;
        return (
          <HintTip
            content={details.map((d) => (
              <p key={d}>{d}</p>
            ))}
          >
            <span
              tabIndex={0}
              className="cursor-help whitespace-nowrap font-medium underline decoration-dotted underline-offset-4"
            >
              {bs(displayPrice)}
            </span>
          </HintTip>
        );
      },
      size: 160,
    },
    {
      id: 'registration_status',
      header: ({ column }) => <SortableColumnHeader column={column} title="Inscripción" />,
      accessorFn: (row) => row.registration_status,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <StatusPill look={registrationLook(p.registration_status)} />
            {p.registration_source === 'PUBLIC' && (
              <HintTip content="Se inscribió desde el formulario público">
                <span tabIndex={0} className="text-muted-foreground" aria-label="Formulario público">
                  <Globe className="size-3.5" />
                </span>
              </HintTip>
            )}
            {canEditForm && needsSignature(p, signatureMode) && (
              <Stop>
                <HintTip content="Sin firma. Clic para pedirla.">
                  <button
                    type="button"
                    aria-label="Pedir firma"
                    onClick={() => onRequestSignature(p)}
                    className="rounded p-0.5 text-amber-600 hover:bg-amber-500/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:text-amber-400"
                  >
                    <PenLine className="size-4" />
                  </button>
                </HintTip>
              </Stop>
            )}
            {p.registration_status === 'PRE_REGISTERED' && (
              <Stop>
                <RowActionButton onClick={() => onReview(p)}>Revisar</RowActionButton>
              </Stop>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: 'payment_status',
      header: ({ column }) => <SortableColumnHeader column={column} title="Pago" />,
      accessorFn: (row) => row.payment_status,
      cell: ({ row }) => {
        const p = row.original;
        const canCharge = canOperate && p.payment_status !== 'PAID';
        const missingReceipt = canIssueReceipts ? getReceiptInfo(p)?.paymentWithoutReceipt : undefined;
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <StatusPill look={paymentLook(p.payment_status)} />
            {missingReceipt && (
              <Stop>
                <HintTip content="Pago sin recibo. Clic para emitirlo.">
                  <button
                    type="button"
                    aria-label="Emitir recibo"
                    onClick={() => onIssueReceipt(p, missingReceipt)}
                    className="rounded p-0.5 text-amber-600 hover:bg-amber-500/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:text-amber-400"
                  >
                    <Receipt className="size-4" />
                  </button>
                </HintTip>
              </Stop>
            )}
            {canCharge && (
              <Stop>
                <RowActionButton onClick={() => onRequestPayment(p)}>
                  {p.payment_status === 'PARTIAL' ? 'Cobrar saldo' : 'Cobrar'}
                </RowActionButton>
              </Stop>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: 'attendance',
      header: ({ column }) => <SortableColumnHeader column={column} title="Asistencia" />,
      accessorFn: (row) => row.attendance_status,
      cell: ({ row }) => <StatusPill look={attendanceLook(row.original.attendance_status)} />,
      size: 130,
    },
    {
      id: 'registration_date',
      header: ({ column }) => <SortableColumnHeader column={column} title="Fecha de registro" />,
      accessorFn: (row) => row.registration_date,
      cell: ({ row }) => {
        const date = new Date(row.original.registration_date);
        return <span className="text-xs">{date.toLocaleDateString('es-BO')}</span>;
      },
      size: 120,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Stop>
          <ParticipantActionsMenu groups={getActions(row.original)} disabled={isUpdating} />
        </Stop>
      ),
      size: 60,
    },
  ];
}
